"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  FILM_CUTS,
  FILM_DURATION_LABEL,
  VERTICAL_CUT_QUERY,
  chooseCut,
  type FilmCut,
} from "@/lib/product-film";
import styles from "./ProductFilm.module.css";

type Phase = "idle" | "playing" | "ended";

/**
 * Click-to-play product film.
 *
 * At rest it is a poster in an aspect-ratio box with a play button; the
 * <video> has no src, so nothing downloads. The first tap picks the cut for
 * the screen (vertical on an upright phone, landscape elsewhere), sets the
 * src and calls play() inside the same tap, which is what iOS needs to allow
 * sound. Native controls take over from there (including fullscreen). It
 * never autoplays, never locks or moves the page scroll, and only pauses
 * itself when it has scrolled fully out of view.
 */
export default function ProductFilm() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [cut, setCut] = useState<FilmCut | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");

  const start = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (!cut) {
      const chosen = chooseCut(
        typeof window.matchMedia === "function"
          ? (query) => window.matchMedia(query)
          : undefined
      );
      video.poster = FILM_CUTS[chosen].poster;
      video.src = FILM_CUTS[chosen].src;
      setCut(chosen);
    } else {
      video.currentTime = 0;
    }

    video.controls = true;
    setPhase("playing");
    // If the browser still refuses (rare with a real tap), the native
    // controls are showing, so the visitor can start it from there.
    video.play()?.catch(() => {});
  }, [cut]);

  // Hand keyboard focus to the player once the overlay button is gone,
  // without scrolling the page.
  useEffect(() => {
    if (phase !== "playing") return;
    videoRef.current?.focus({ preventScroll: true });
  }, [phase]);

  // Optional nicety: pause once the film has scrolled completely out of view.
  // A passive observer; it never touches scrolling. Fullscreen and
  // picture-in-picture playback are left alone.
  useEffect(() => {
    if (phase !== "playing") return;
    const video = videoRef.current;
    if (!video || typeof IntersectionObserver === "undefined") return;

    // Only pause after the film has actually been on screen since play began:
    // a keyboard press can start it while the page is still scrolling it into
    // view, and that first "not visible" report must not stop it.
    let seen = false;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        if (entry.isIntersecting) {
          seen = true;
          return;
        }
        if (!seen || video.paused) return;
        const doc = document as Document & {
          webkitFullscreenElement?: Element | null;
        };
        const webkitVideo = video as HTMLVideoElement & {
          webkitDisplayingFullscreen?: boolean;
        };
        if (
          doc.fullscreenElement ||
          doc.webkitFullscreenElement ||
          webkitVideo.webkitDisplayingFullscreen ||
          (typeof document.pictureInPictureElement !== "undefined" &&
            document.pictureInPictureElement === video)
        ) {
          return;
        }
        video.pause();
      },
      { threshold: 0 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [phase]);

  const label =
    phase === "ended"
      ? "Watch the Ethos Virtual film again, with sound"
      : `Play the Ethos Virtual film, ${FILM_DURATION_LABEL} with sound`;

  return (
    <div className={styles.frame} data-cut={cut ?? undefined}>
      <video
        ref={videoRef}
        className={styles.video}
        playsInline
        preload="none"
        aria-label="Ethos Virtual product film"
        aria-hidden={phase === "idle" ? true : undefined}
        onEnded={() => setPhase("ended")}
      />

      {phase !== "playing" && (
        <button
          type="button"
          className={styles.overlay}
          onClick={start}
          aria-label={label}
        >
          {cut ? (
            // eslint-disable-next-line @next/next/no-img-element -- static poster; next/image cannot art-direct two cuts without a second request
            <img
              className={styles.poster}
              src={FILM_CUTS[cut].poster}
              width={FILM_CUTS[cut].width}
              height={FILM_CUTS[cut].height}
              alt=""
              decoding="async"
            />
          ) : (
            <picture>
              <source
                media={VERTICAL_CUT_QUERY}
                srcSet={FILM_CUTS.vertical.poster}
                width={FILM_CUTS.vertical.width}
                height={FILM_CUTS.vertical.height}
              />
              <img
                className={styles.poster}
                src={FILM_CUTS.landscape.poster}
                width={FILM_CUTS.landscape.width}
                height={FILM_CUTS.landscape.height}
                alt=""
                loading="lazy"
                decoding="async"
              />
            </picture>
          )}
          <span className={styles.scrim} aria-hidden="true" />
          <span className={styles.cta} aria-hidden="true">
            <span className={styles.icon}>
              {phase === "ended" ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12a9 9 0 1 0 3-6.7" />
                  <path d="M3 4v5h5" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5.14v13.72a1 1 0 0 0 1.52.85l10.95-6.86a1 1 0 0 0 0-1.7L9.52 4.29A1 1 0 0 0 8 5.14z" />
                </svg>
              )}
            </span>
            {/* Icon and running time only (owner, 2026-09-29: no label text).
                The button's aria-label still says what it does. */}
            <span className={styles.duration}>{FILM_DURATION_LABEL}</span>
          </span>
        </button>
      )}
    </div>
  );
}
